import React, { useEffect, useState } from 'react';
import { Modal } from '@/components/modal/Modal';
import { TextInput } from '@/components/form/fields/text/TextInput';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AddressRequest, AddressDto } from '@/services/api/contracts/address';
import { AddressAutocomplete, AutocompleteAddressData } from './AddressAutocomplete';
import { useDirtyFormGuard } from '@/hooks/useDirtyFormGuard';
import { UnsavedChangesModal } from '@/components/unsavedChanges/UnsavedChangesModal';

export interface AddressFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit' | 'manual';
  initialData?: AddressDto | AutocompleteAddressData | null;
  onSave: (finalAddress: AddressRequest) => Promise<void>;
  title?: string;
}

export const AddressFormModal: React.FC<AddressFormModalProps> = ({
  isOpen,
  onClose,
  mode,
  initialData,
  onSave,
  title
}) => {
  const { control, handleSubmit, reset, setValue, formState: { isSubmitting, errors, isDirty } } = useForm<AddressRequest>({
    resolver: zodResolver(AddressRequest),
    defaultValues: {
      name: '',
      line1: '',
      line2: '',
      city: '',
      state: '',
      postcode: '',
      country: ''
    }
  });

  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  useDirtyFormGuard(isDirty);

  useEffect(() => {
    if (isOpen) {
      reset({
        name: (initialData as AddressDto)?.name || '',
        line1: initialData?.line1 || '',
        line2: initialData?.line2 || '',
        city: initialData?.city || '',
        state: initialData?.state || '',
        postcode: initialData?.postcode || '',
        country: initialData?.country || 'UK'
      });
      setShowUnsavedWarning(false);
    }
  }, [isOpen, initialData, reset]);

  const onSubmit = async (data: AddressRequest) => {
    try {
      await onSave(data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAutocompleteChange = (addressData: AutocompleteAddressData) => {
    setValue('line1', addressData.line1, { shouldDirty: true, shouldValidate: true });
    setValue('line2', addressData.line2 || '', { shouldDirty: true, shouldValidate: true });
    setValue('city', addressData.city, { shouldDirty: true, shouldValidate: true });
    setValue('state', addressData.state || '', { shouldDirty: true, shouldValidate: true });
    setValue('postcode', addressData.postcode, { shouldDirty: true, shouldValidate: true });
    setValue('country', addressData.country || 'UK', { shouldDirty: true, shouldValidate: true });
  };

  const handleClose = () => {
    if (isDirty) {
      setShowUnsavedWarning(true);
    } else {
      onClose();
    }
  };

  const modalTitle = title || (mode === 'edit' ? 'Edit Address' : mode === 'manual' ? 'Manually Enter Address' : 'Add New Address');

  return (
    <>
      <Modal isOpen={isOpen} open={() => {}} close={handleClose} className="max-w-[500px] bg-white p-6 rounded-lg overflow-visible">
        <Modal.Controls close={handleClose} isOpen={isOpen} open={() => {}}>
          <h2 className="text-lg font-semibold">{modalTitle}</h2>
        </Modal.Controls>
        <form 
          onSubmit={(e) => {
            e.stopPropagation();
            handleSubmit(onSubmit)(e);
          }} 
          className="flex flex-col gap-4 py-4 text-slate-800 dark:text-slate-100"
        >
          {mode === 'add' && (
            <div className="mb-2 p-3 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
              <AddressAutocomplete 
                label="Search Address" 
                onChange={handleAutocompleteChange} 
                placeholder="Start typing to autofill..."
              />
            </div>
          )}

          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Location Name / Alias</label>
                <TextInput 
                  id="form-name" 
                  value={field.value} 
                  onChange={field.onChange} 
                  placeholder="e.g. Headquarters, Warehouse"
                />
                {errors.name && <span className="text-red-500 text-xs mt-1">{errors.name.message}</span>}
              </div>
            )}
          />
          
          <Controller
            name="line1"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Line 1</label>
                <TextInput id="form-line1" value={field.value} onChange={field.onChange} />
                {errors.line1 && <span className="text-red-500 text-xs mt-1">{errors.line1.message}</span>}
              </div>
            )}
          />

          <Controller
            name="line2"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Line 2</label>
                <TextInput id="form-line2" value={field.value || ''} onChange={field.onChange} />
              </div>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="block text-sm font-medium mb-1">City</label>
                  <TextInput id="form-city" value={field.value} onChange={field.onChange} />
                  {errors.city && <span className="text-red-500 text-xs mt-1">{errors.city.message}</span>}
                </div>
              )}
            />

            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="block text-sm font-medium mb-1">State / Province</label>
                  <TextInput id="form-state" value={field.value || ''} onChange={field.onChange} />
                </div>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="postcode"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="block text-sm font-medium mb-1">Postcode</label>
                  <TextInput id="form-postcode" value={field.value} onChange={field.onChange} />
                  {errors.postcode && <span className="text-red-500 text-xs mt-1">{errors.postcode.message}</span>}
                </div>
              )}
            />

            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="block text-sm font-medium mb-1">Country</label>
                  <TextInput id="form-country" value={field.value || ''} onChange={field.onChange} />
                </div>
              )}
            />
          </div>

          <div className="flex justify-end space-x-2 mt-4">
            <Button variant={ButtonVariant.Secondary} onClick={handleClose} disabled={isSubmitting} type="button">Cancel</Button>
            <Button variant={ButtonVariant.Primary} type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </Modal>

      <UnsavedChangesModal
        isOpen={showUnsavedWarning}
        open={() => setShowUnsavedWarning(true)}
        close={() => setShowUnsavedWarning(false)}
        onCancelCallback={() => setShowUnsavedWarning(false)}
        onConfirmCallback={() => {
          setShowUnsavedWarning(false);
          onClose();
        }}
        title="Abandon Changes"
        message="You have unsaved changes. Are you sure you want to leave?"
        confirmText="Yes, Abandon"
        confirmColorClass="bg-secondary-600 hover:bg-secondary-700 text-white"
        cancelText="No, Continue"
        cancelColorClass="bg-primary-700 hover:bg-primary-800 text-white"
      />
    </>
  );
};
